/**
 * Promise-based confirm / alert modals — a single "SweetAlert-style"
 * replacement for native window.confirm / window.alert.
 *
 * Usage:
 *   import { confirm, alert } from '@/lib/confirm'
 *
 *   if (await confirm({
 *     title: 'Cancel subscription?',
 *     description: 'The customer loses access immediately.',
 *     variant: 'danger',
 *     confirmLabel: 'Cancel it',
 *   })) {
 *     cancel.mutate()
 *   }
 *
 *   await alert({ title: 'Saved', description: 'Changes are live.' })
 *
 * A single <ConfirmHost /> is mounted once in DashboardLayout — it
 * subscribes to the store and renders the active request's modal.
 * Requests queue (not stack) so only one modal is ever on screen.
 */

import { useEffect } from 'react'
import { create } from 'zustand'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Icon, type IconVariant } from '@/components/ui/icon'
import { cn } from '@/lib/utils'

export type ConfirmVariant = 'default' | 'danger' | 'warn' | 'info' | 'success'

export interface ConfirmOptions {
  title: string
  description?: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: ConfirmVariant
  icon?: string
  iconVariant?: IconVariant
}

export interface AlertOptions {
  title: string
  description?: string
  dismissLabel?: string
  variant?: ConfirmVariant
  icon?: string
  iconVariant?: IconVariant
}

interface ActiveRequest {
  id: number
  kind: 'confirm' | 'alert'
  options: ConfirmOptions | AlertOptions
  resolve: (value: boolean) => void
}

interface ConfirmState {
  queue: ActiveRequest[]
  enqueue: (req: Omit<ActiveRequest, 'id'>) => number
  resolveHead: (value: boolean) => void
}

let nextId = 1
const store = create<ConfirmState>((set, get) => ({
  queue: [],
  enqueue: (req) => {
    const id = nextId++
    set((s) => ({ queue: [...s.queue, { ...req, id }] }))
    return id
  },
  resolveHead: (value) => {
    const head = get().queue[0]
    if (!head) return
    head.resolve(value)
    set((s) => ({ queue: s.queue.slice(1) }))
  },
}))

export function confirm(options: ConfirmOptions): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    store.getState().enqueue({ kind: 'confirm', options, resolve })
  })
}

export function alert(options: AlertOptions): Promise<void> {
  return new Promise<void>((resolve) => {
    store.getState().enqueue({
      kind: 'alert',
      options,
      resolve: () => resolve(),
    })
  })
}

/** Mounted once — subscribes to the queue and renders the head. */
export function ConfirmHost() {
  const head = store((s) => s.queue[0])
  const resolveHead = store((s) => s.resolveHead)

  // Escape-to-cancel for confirm (already handled by Radix Dialog,
  // but we need to resolve the promise explicitly on external close).
  useEffect(() => {
    if (!head) return
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        resolveHead(false)
      }
    }
    document.addEventListener('keydown', onEscape)
    return () => document.removeEventListener('keydown', onEscape)
  }, [head, resolveHead])

  if (!head) return null
  return head.kind === 'confirm' ? (
    <ConfirmModal
      options={head.options as ConfirmOptions}
      onResolve={resolveHead}
    />
  ) : (
    <AlertModal options={head.options as AlertOptions} onResolve={resolveHead} />
  )
}

const VARIANT_ICON: Record<ConfirmVariant, string> = {
  default: 'circle-question',
  danger: 'triangle-exclamation',
  warn: 'triangle-exclamation',
  info: 'circle-info',
  success: 'circle-check',
}

const VARIANT_TONE: Record<
  ConfirmVariant,
  {
    iconColor: string
    iconBg: string
    btnVariant: 'primary' | 'danger'
  }
> = {
  default: {
    iconColor: 'text-primary-ink',
    iconBg: 'bg-primary-tint',
    btnVariant: 'primary',
  },
  danger: {
    iconColor: 'text-danger',
    iconBg: 'bg-danger/10',
    btnVariant: 'danger',
  },
  warn: {
    iconColor: 'text-warn',
    iconBg: 'bg-warn/10',
    btnVariant: 'primary',
  },
  info: {
    iconColor: 'text-primary-ink',
    iconBg: 'bg-primary-tint',
    btnVariant: 'primary',
  },
  success: {
    iconColor: 'text-success',
    iconBg: 'bg-success/10',
    btnVariant: 'primary',
  },
}

function ConfirmModal({
  options,
  onResolve,
}: {
  options: ConfirmOptions
  onResolve: (v: boolean) => void
}) {
  const variant = options.variant ?? 'default'
  const tone = VARIANT_TONE[variant]
  const iconName = options.icon ?? VARIANT_ICON[variant]

  return (
    <Dialog
      open={true}
      onOpenChange={(open) => {
        if (!open) onResolve(false)
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader className="mb-3">
          <div className="flex items-start gap-3">
            <div
              className={cn(
                'h-10 w-10 rounded-full flex items-center justify-center shrink-0',
                tone.iconBg,
              )}
            >
              <Icon
                name={iconName}
                variant={options.iconVariant ?? 'solid'}
                className={cn('text-base', tone.iconColor)}
              />
            </div>
            <div className="flex-1 pt-0.5">
              <DialogTitle>{options.title}</DialogTitle>
              {options.description && (
                <DialogDescription className="mt-1.5 leading-relaxed">
                  {options.description}
                </DialogDescription>
              )}
            </div>
          </div>
        </DialogHeader>

        <footer className="flex items-center justify-end gap-2 pt-2 border-t border-hairline">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => onResolve(false)}
            autoFocus={variant !== 'danger'}
          >
            {options.cancelLabel ?? 'Cancel'}
          </Button>
          <Button
            type="button"
            variant={tone.btnVariant}
            size="sm"
            onClick={() => onResolve(true)}
            autoFocus={variant === 'danger' ? false : undefined}
          >
            {options.confirmLabel ??
              (variant === 'danger' ? 'Delete' : 'Confirm')}
          </Button>
        </footer>
      </DialogContent>
    </Dialog>
  )
}

function AlertModal({
  options,
  onResolve,
}: {
  options: AlertOptions
  onResolve: (v: boolean) => void
}) {
  const variant = options.variant ?? 'info'
  const tone = VARIANT_TONE[variant]
  const iconName = options.icon ?? VARIANT_ICON[variant]

  return (
    <Dialog
      open={true}
      onOpenChange={(open) => {
        if (!open) onResolve(true)
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader className="mb-3">
          <div className="flex items-start gap-3">
            <div
              className={cn(
                'h-10 w-10 rounded-full flex items-center justify-center shrink-0',
                tone.iconBg,
              )}
            >
              <Icon
                name={iconName}
                variant={options.iconVariant ?? 'solid'}
                className={cn('text-base', tone.iconColor)}
              />
            </div>
            <div className="flex-1 pt-0.5">
              <DialogTitle>{options.title}</DialogTitle>
              {options.description && (
                <DialogDescription className="mt-1.5 leading-relaxed">
                  {options.description}
                </DialogDescription>
              )}
            </div>
          </div>
        </DialogHeader>

        <footer className="flex items-center justify-end pt-2 border-t border-hairline">
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={() => onResolve(true)}
            autoFocus
          >
            {options.dismissLabel ?? 'OK'}
          </Button>
        </footer>
      </DialogContent>
    </Dialog>
  )
}
